import { InstallationData } from "@/installation/installation";
import { Zap, ZapOff } from "lucide-react";
import React, { Suspense } from "react";
import { useDatumCardSuspenseQuery } from "@/components/datum-card-query";
import { ApiPath } from "@/api/path";
import { LAST_30_DAYS } from "@/dateRanges/dateRanges";
import { ONE_DAY } from "@/dateRanges/windowSizes";
import { getDatum, SeriesDatumName } from "@/components/charts/lineChartResponse";
import { OFF } from "@/dateRanges/refreshIntervals";
import {
  electricityConsumptionUnits,
  useElectricityConsumptionUnitContext
} from "@/energyPrices/ElectricityConsumptionUnitContext";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { ElectricityPriceTooltipMessage } from "@/energyPrices/electricityPrice";
import { LocationData } from "@/location/location";
import formatValueAndUnit from "@/metrics/formatValueAndUnit";
import { ErrorBoundary } from "react-error-boundary";

const AssetsEnergyConsumptionMessage = ({ assets }: { assets: InstallationData[] }) => {

  const { electricityConsumptionUnit } = useElectricityConsumptionUnitContext();

  const energyConsumption = useDatumCardSuspenseQuery(
    ApiPath.INSTALLATION_REFRIGERANT_AGGREGATED_ENERGY,
    {
      dateRange: LAST_30_DAYS,
      windowSize: ONE_DAY,
      installation_id: assets.map((asset) => asset.id),
      target_unit: electricityConsumptionUnit
    },
    getDatum("overall", SeriesDatumName.SUM),
    OFF
  )

  const [valueAndUnitFirst, valueAndUnitSecond] = formatValueAndUnit(
    energyConsumption,
    electricityConsumptionUnit,
    value => value.toLocaleString(),
    unit => electricityConsumptionUnits[unit]
  )

  return (
    <span className="whitespace-nowrap">
      {valueAndUnitFirst}
      {electricityConsumptionUnit === "USD" ? "" : " "}
      {valueAndUnitSecond}
      {" "}
      (last 30 days)
    </span>
  )
}

const AssetsEnergyConsumptionUnitSwitcher = ({ location }: { location: LocationData }) => {

  const { electricityConsumptionUnit, setElectricityConsumptionUnit } = useElectricityConsumptionUnitContext();

  const otherUnit = electricityConsumptionUnit === "USD" ? "kilowatt_hour" : "USD"

  return (
    <div className="flex flex-col gap-2">
      {
        electricityConsumptionUnit === "USD" && (
          <ErrorBoundary fallback="couldn't load electricity prices">
            <Suspense fallback="loading">
              <ElectricityPriceTooltipMessage location={location}/>
            </Suspense>
          </ErrorBoundary>
        )
      }
      <Button
        size="sm"
        variant="outline"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setElectricityConsumptionUnit(otherUnit);
        }}
      >
        Switch to {electricityConsumptionUnits[otherUnit]}
      </Button>
    </div>
  )
}

export const AssetsEnergyConsumptionCard = ({ assets, location }: { assets: InstallationData[], location: LocationData }) => {

  const { electricityConsumptionUnit } = useElectricityConsumptionUnitContext();

  return (
    <div className="px-2 py-1 rounded-md flex gap-2 items-center bg-input/50 text-gray-500 w-fit">
      <TooltipProvider delayDuration={100}>
        <Tooltip>
          <TooltipTrigger className="flex gap-2 items-center">
            <ErrorBoundary
              fallback={
                <ZapOff size={15}/>
              }
              resetKeys={[electricityConsumptionUnit]}
            >
              <Zap size={15} className="fill-gray-500"/>
              <Suspense fallback="Loading">
                <AssetsEnergyConsumptionMessage assets={assets}/>
              </Suspense>
            </ErrorBoundary>
          </TooltipTrigger>
          <TooltipContent side={"top"} align={"center"} className="p-4">
            <AssetsEnergyConsumptionUnitSwitcher location={location}/>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}
